import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Save, X } from "lucide-react";
import { useForm, UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useUserSchema } from "./userSchema";

import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { RadioGroup, RadioGroupCard } from "@/components/ui/v2/radio-group";
import { CreateUserRequest } from "@/types/request/CreateUserRequest";
import { EditUserRequest } from "@/types/request/EditUserRequest";
import { UserType } from "@/types/user/userType";

export type UserFormContentData = {
  email: string;
  role: string;
  firstName: string;
  infix: string | null;
  lastName: string;
};

type UserFormOptions = {
  value: string;
  label: string;
  description: string;
};

// Base props shared by both add and edit forms
type BaseUserFormContentProps = {
  options: UserFormOptions[];
  isRoleDisabled?: boolean;
  isSubmitting?: boolean;
  onSuccess?: () => void;
};

// Discriminated union: create form vs edit form
type CreateUserFormContentProps = BaseUserFormContentProps & {
  type: "create";
  user?: UserType;
  onSubmitForm?: (
    data: CreateUserRequest,
    setError?: UseFormSetError<UserFormContentData>,
  ) => void;
};

/** Edit form submits only form fields; page adds clientId and tenantId when calling the API. */
export type EditUserFormPayload = Omit<
  EditUserRequest,
  "clientId" | "tenantId"
>;

type EditUserFormContentProps = BaseUserFormContentProps & {
  type: "edit";
  user: UserType;
  onSubmitForm?: (data: EditUserFormPayload) => void;
};

type UserFormContentProps =
  | CreateUserFormContentProps
  | EditUserFormContentProps;

const UserFormContent = (props: UserFormContentProps) => {
  const { t } = useTranslation();
  const { userSchema } = useUserSchema();

  const defaultValues: UserFormContentData = {
    email: props.user?.email || "",
    role: props.user?.roleName || "",
    firstName: props.user?.firstName || "",
    infix: props.user?.infix === "" ? null : props.user?.infix || null,
    lastName: props.user?.lastName || "",
  };

  const methods = useForm<UserFormContentData>({
    resolver: zodResolver(userSchema),
    mode: "onChange",
    defaultValues,
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = handleSubmit((formData) => {
    if (props.type === "edit") {
      if (!props.onSubmitForm) {
        return;
      }

      const payload: EditUserFormPayload = {
        email: formData.email,
        role: formData.role,
        firstName: formData.firstName,
        infix: formData.infix === "" ? null : formData.infix,
        lastName: formData.lastName,
      };

      props.onSubmitForm(payload);
      return;
    }

    // Create form
    if (!props.onSubmitForm) {
      return;
    }

    const payload: CreateUserRequest = {
      email: formData.email,
      role: formData.role,
      firstName: formData.firstName,
      infix: formData.infix === "" ? null : formData.infix,
      lastName: formData.lastName,
    };

    props.onSubmitForm(payload, setError);
  });

  const handleCancelButton = () => {
    if (props.onSuccess) {
      props.onSuccess();
    }
  };

  return (
    <Form {...methods}>
      <form onSubmit={onSubmit} className="max-w-lg">
        <div className="space-y-6">
          <FormField
            control={methods.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.role")}</FormLabel>
                <FormControl>
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    {props.options.map((option) => (
                      <RadioGroupCard
                        key={option.value}
                        id={`role-${option.value}`}
                        value={option.value}
                        label={option.label}
                        description={option.description}
                        disabled={props.isRoleDisabled}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormDescription>
                  {t("admin.userManagement.addUser.roleDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.firstName")}</FormLabel>
                <Input {...field} placeholder={t("common.firstName")} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="infix"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("common.infix")}{" "}
                  <span className="text-muted-foreground text-sm font-normal">
                    ({t("common.optional")})
                  </span>
                </FormLabel>
                <Input
                  {...field}
                  value={field.value || ""}
                  placeholder={t("common.infix")}
                />
                <FormDescription>
                  {t("admin.userManagement.addUser.infixDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.lastName")}</FormLabel>
                <Input {...field} placeholder={t("common.lastName")} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.email")}</FormLabel>
                <Input
                  {...field}
                  disabled={props.type === "edit"}
                  placeholder={t("common.email")}
                  type="email"
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex items-center justify-start gap-2 mt-6">
          <Button type="submit" disabled={props.isSubmitting}>
            {props.isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {props.type === "edit"
              ? t("common.save")
              : t("admin.userManagement.addUser.saveUser")}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancelButton}>
            <X className="size-4" />
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UserFormContent;
export type { UserFormContentProps, UserFormOptions };
